import * as React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, SubtleButton } from "components/button";
import { theme } from "styles/theme";
import type { AssigneeConflict } from "./checkAppointmentConflicts";
import { AppointmentConflictConfirmBody } from "./appointmentConflictMessages";

type Props = {
  conflicts: AssigneeConflict[];
  timezone: string;
  onEdit: () => void;
  onCancel: () => void;
  onConfirm: () => void;
  isConfirming?: boolean;
};

export function AppointmentSubmitConflictConfirm({
  conflicts,
  timezone,
  onEdit,
  onCancel,
  onConfirm,
  isConfirming,
}: Props) {
  const { t } = useTranslation();
  const tModal =
    "containers.appointmentCard.appointment-folder.appointmentModal";
  const tSure = "components.areYouSure";
  const trModal = (key: string) => t(`${tModal}.${key}`);
  const trSure = (key: string) => t(`${tSure}.${key}`);

  return (
    <ConfirmShell role="dialog" aria-labelledby="appointment-conflict-confirm-title">
      <Title id="appointment-conflict-confirm-title">
        {trSure("Confirmation")}
      </Title>
      <AppointmentConflictConfirmBody
        conflicts={conflicts}
        timezone={timezone}
      />
      <Footer>
        <SubtleButton type="button" onClick={onEdit}>
          {trModal("Edit appointment")}
        </SubtleButton>
        <FooterActions>
          <SubtleButton type="button" onClick={onCancel}>
            {trSure("Cancel")}
          </SubtleButton>
          <Button
            type="button"
            primary
            isLoading={isConfirming}
            onClick={onConfirm}
          >
            {trSure("Confirm")}
          </Button>
        </FooterActions>
      </Footer>
    </ConfirmShell>
  );
}

const ConfirmShell = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  padding: 0.25rem 0;
`;

const Title = styled.h2`
  margin: 0 0 0.75rem;
  font-size: ${theme.fontSize.large};
  font-weight: bold;
  color: ${theme.color.text};
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;
